import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import { ApiParameter } from "@insite/client-framework/Services/ApiService";
import { getCurrentWebsite } from "@insite/client-framework/Services/WebsiteService";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import UCSColors from "@ucs/Utils/UCSColors";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {
    extendedStyles?: FooterContainerStyles;
}

interface GetCurrentWebsiteApiParameter extends ApiParameter {
    expand?: ("languages" | "currencies" | "countries" | "states")[];
    additionalExpands?: string[];
}
export const siteParams: GetCurrentWebsiteApiParameter = {};

export interface FooterContainerStyles {
    widgetGridContainer?: GridContainerProps;
    logoGridItem?: GridItemProps;
    logoWrapper?: InjectableCss;
    socialLinksWrapper?: InjectableCss;
    socialLinksGridItem?: GridItemProps;
    linkListsGridItem?: GridItemProps;
    contactGridItem?: GridItemProps;
    legalSectionGridItem?: GridItemProps;
    legalSectionCopyWrapper?: InjectableCss;
    legalLinksWrapper?: InjectableCss;
    statementGridItem?: GridItemProps;
}

export const footerContainerStyles: FooterContainerStyles = {
    widgetGridContainer: {
        gap: 5,
        css: css`
            background-color: ${UCSColors.gray01};
            color: ${UCSColors.gray10};
            max-width: 100%;
            margin: 0 auto;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        padding: 33px 20px 32px;
                    `,
                    css`
                        padding: 64px 0 32px;
                    `,
                    css`
                        padding: 64px 60px 32px;
                    `,
                    css`
                        padding: 65px 96px 32px;
                    `,
                    css`
                        padding: 65px 110px 32px;
                    `,
                ]);
            }}
        `,
    },
    logoGridItem: {
        width: [12, 12, 12, 5, 5],
        css: css`
            width: 100%;
            text-transform: uppercase;
            display: flex;
            position: relative;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        justify-content: center;
                    `,
                    css`
                        flex-direction: row;
                        margin-bottom: 25px !important;
                    `,
                    css`
                        flex-direction: row;
                        margin-bottom: 25px !important;
                    `,
                    css`
                        flex-direction: column;
                    `,
                    css`
                        flex-direction: column;
                    `,
                ]);
            }}
        `,
    },
    logoWrapper: {
        css: css`
            max-width: 225px;
            margin-bottom: 25px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css``,
                    css`
                        margin-bottom: 0;
                    `,
                    css`
                        margin-bottom: 0;
                    `,
                    css``,
                    css``,
                ]);
            }}
        `,
    },
    socialLinksWrapper: {
        css: css`
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css``,
                    css`
                        margin: auto 0 auto auto;
                    `,
                    css`
                        margin: auto 0 auto auto;
                    `,
                    css``,
                    css``,
                ]);
            }}
        `,
    },
    socialLinksGridItem: {
        width: [12, 6, 6, 6, 6],
        css: css`
            width: 100%;
            display: flex;
            justify-content: flex-end;
            align-items: center;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        justify-content: flex-start;
                    `,
                    css`
                        justify-content: flex-end;
                    `,
                    css``,
                    css``,
                    css``,
                ]);
            }}
        `,
    },
    linkListsGridItem: {
        width: [12, 4, 4, 2, 2],
        css: css`
            width: 100%;
            display: flex;
            flex-direction: column;
            font-size: 16px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        padding-top: 0;
                    `,
                    css`
                        padding-top: 0;
                    `,
                    css`
                        padding-top: 0;
                    `,
                    css``,
                    css``,
                ]);
            }}
        `,
    },
    contactGridItem: {
        width: [12, 4, 4, 3, 3],
        css: css`
            width: 100%;
            display: flex;
            flex-direction: column;
            font-size: 16px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        padding-top: 0;
                    `,
                    css`
                        padding-top: 0;
                    `,
                    css`
                        padding-top: 0;
                    `,
                    css``,
                    css``,
                ]);
            }}
        `,
    },
    legalSectionGridItem: {
        width: 12,
        css: css`
            font-size: 12px;
            color: ${UCSColors.gray07};
            display: flex;
            flex-wrap: wrap;
            padding-top: 48px;
            padding-bottom: 24px;
        `,
    },
    legalSectionCopyWrapper: {
        css: css`
            padding-top: 1px;
            margin-bottom: 5px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        display: block;
                        width: 100%;
                    `,
                    css`
                        display: block;
                        width: 100%;
                    `,
                    css`
                        display: block;
                        width: 100%;
                    `,
                    css`
                        display: block;
                        width: 100%;
                    `,
                    css`
                        margin-right: 20px;
                    `,
                ]);
            }}
        `,
    },
    legalLinksWrapper: {
        css: css`
            min-width: fit-content;
            margin-right: 20px;
        `,
    },
    statementGridItem: {
        width: 12,
        color: `${UCSColors.gray03}`,
    },
};

const year = new Date().getFullYear();

const FooterContainer: FC<OwnProps> = ({ id, extendedStyles }) => {
    const [styles] = React.useState(() => mergeToNew(footerContainerStyles, extendedStyles));
    const [site] = React.useState(() => getCurrentWebsite(siteParams));

    return (
        <GridContainer {...styles.widgetGridContainer}>
            <GridItem {...styles.logoGridItem}>
                <StyledWrapper {...styles.logoWrapper}>
                    <Zone zoneName="Logo" contentId={id} />
                </StyledWrapper>
                <StyledWrapper {...styles.socialLinksWrapper}>
                    <Hidden below="sm">
                        <Zone zoneName="SocialLinks" contentId={id} />
                    </Hidden>
                </StyledWrapper>
            </GridItem>

            <GridItem {...styles.linkListsGridItem}></GridItem>

            <GridItem {...styles.linkListsGridItem}>
                <Zone zoneName="LinkList2" contentId={id} />
            </GridItem>

            <GridItem {...styles.contactGridItem}>
                <Zone zoneName="ContactUs" contentId={id} />
            </GridItem>

            <GridItem {...styles.socialLinksGridItem}>
                <Hidden above="xs" style={{ paddingTop: 48 }}>
                    <Zone zoneName="SocialLinks" contentId={id} />
                </Hidden>
            </GridItem>

            <GridItem {...styles.legalSectionGridItem}>
                <StyledWrapper {...styles.legalSectionCopyWrapper}>
                    <Typography>&copy; Copyright {year} UCS Forest Group. All Rights Reserved.</Typography>
                </StyledWrapper>
                <StyledWrapper {...styles.legalLinksWrapper}>
                    <Zone zoneName="LegalLinks" contentId={id} />
                </StyledWrapper>
            </GridItem>

            <GridItem {...styles.statementGridItem}>
                <StyledWrapper>
                    <Zone zoneName="Statement" contentId={id} />
                </StyledWrapper>
            </GridItem>
        </GridContainer>
    );
};

const footerContainer: WidgetModule = {
    component: FooterContainer,
    definition: {
        group: "Footer",
        allowedContexts: ["Footer"],
    },
};

export default footerContainer;
